import {
  CategoryAreaPinComponentCategoryThemeSettingsSchema,
  PinComponent,
  PinThemeEntry,
  PinThemePinComponentSettings,
  TypographyRecord,
} from '@lessonup/pins-shared';
import { merge } from 'lodash';
import { defaultPinTheme } from './defaultPinTheme';
import {
  PinThemePinComponentSettingsHandlers,
  getTypography,
  pinThemePinComponentSchemaReturnTypes,
} from './pinThemePinComponentSettingsHandler.utils';

export const getPinThemePinComponentSettingsHandlers = (
  themePinSettings: PinThemeEntry,
  typographyRecord: TypographyRecord
): PinThemePinComponentSettingsHandlers => {
  const pinThemePinComponentSettings = themePinSettings.pinComponents;
  const defaultPinThemePinComponentSettings = defaultPinTheme['default'].pinComponents;
  const handlers: PinThemePinComponentSettingsHandlers = {
    FILL_IN_THE_BLANKS_AREA: ({ settings }) =>
      computeEffectiveSettings(
        settings,
        pinThemePinComponentSettings?.FILL_IN_THE_BLANKS_AREA,
        defaultPinThemePinComponentSettings?.FILL_IN_THE_BLANKS_AREA,
        pinThemePinComponentSchemaReturnTypes.FILL_IN_THE_BLANKS_AREA
      ),
    VIDEO: ({ settings }) =>
      computeEffectiveSettings(
        settings,
        pinThemePinComponentSettings?.VIDEO,
        defaultPinThemePinComponentSettings?.VIDEO,
        pinThemePinComponentSchemaReturnTypes.VIDEO
      ),
    UNSUPPORTED: ({ settings }) =>
      computeEffectiveSettings(
        settings,
        pinThemePinComponentSettings?.UNSUPPORTED,
        defaultPinThemePinComponentSettings?.UNSUPPORTED,
        pinThemePinComponentSchemaReturnTypes.UNSUPPORTED
      ),
    CATEGORY_AREA: ({ settings }) => {
      const effectiveSettings = computeEffectiveSettings(
        settings,
        pinThemePinComponentSettings?.CATEGORY_AREA,
        defaultPinThemePinComponentSettings?.CATEGORY_AREA,
        pinThemePinComponentSchemaReturnTypes.CATEGORY_AREA.filter((s) => s !== 'defaultCategorySyle')
      );

      return {
        ...effectiveSettings,
        categories: settings.categories.map((category) => ({
          ...category,
          style: computeEffectiveGenericObject(
            CategoryAreaPinComponentCategoryThemeSettingsSchema.unwrap().keyof().options,
            category.style,
            pinThemePinComponentSettings?.CATEGORY_AREA?.defaultCategorySyle,
            defaultPinThemePinComponentSettings?.CATEGORY_AREA?.defaultCategorySyle
          ),
        })),
      };
    },
    HOTSPOT: ({ settings }) =>
      computeEffectiveSettings(
        settings,
        pinThemePinComponentSettings?.HOTSPOT,
        defaultPinThemePinComponentSettings?.HOTSPOT,
        pinThemePinComponentSchemaReturnTypes.HOTSPOT
      ),
    AUDIO: ({ settings }) =>
      computeEffectiveSettings(
        settings,
        pinThemePinComponentSettings?.AUDIO,
        defaultPinThemePinComponentSettings?.AUDIO,
        pinThemePinComponentSchemaReturnTypes.AUDIO
      ),
    IMAGE: ({ settings }) =>
      computeEffectiveSettings(
        settings,
        pinThemePinComponentSettings?.IMAGE,
        defaultPinThemePinComponentSettings?.IMAGE,
        pinThemePinComponentSchemaReturnTypes.IMAGE
      ),
    LINE: ({ settings }) =>
      computeEffectiveSettings(
        settings,
        pinThemePinComponentSettings?.LINE,
        defaultPinThemePinComponentSettings?.LINE,
        pinThemePinComponentSchemaReturnTypes.LINE
      ),
    SHAPE: ({ settings }) =>
      computeEffectiveSettings(
        settings,
        pinThemePinComponentSettings?.SHAPE,
        defaultPinThemePinComponentSettings?.SHAPE,
        pinThemePinComponentSchemaReturnTypes.SHAPE
      ),
    TEXT: ({ settings, textEditorHandler, isTextEditMode }) => {
      const textSettings = computeEffectiveSettings(
        settings,
        pinThemePinComponentSettings?.TEXT,
        defaultPinThemePinComponentSettings?.TEXT,
        pinThemePinComponentSchemaReturnTypes.TEXT
      );

      const activeTypography = getTypography(settings, typographyRecord);

      const fontFamilyCursorValue = textEditorHandler?.fontFamily.get('');
      const innerFontFamilyValue = textSettings.fontFamily ?? activeTypography?.fontFamily;
      const fontFamilyValue = isTextEditMode && fontFamilyCursorValue ? fontFamilyCursorValue : innerFontFamilyValue;

      const innerFontSizeValue = textSettings.fontSize ?? activeTypography?.fontSize;
      const fontSizeCursorValue = textEditorHandler?.fontSize.get();
      const fontSize = isTextEditMode && fontSizeCursorValue ? fontSizeCursorValue : innerFontSizeValue;

      const fontColorCursorValue = textEditorHandler?.color.get('');
      const fontColor = isTextEditMode && fontColorCursorValue ? fontColorCursorValue : textSettings.fontColor;

      return {
        ...textSettings,
        typographyKey: textSettings.typographyKey ?? 'normalText',
        fontFamily: fontFamilyValue,
        fontSize,
        fontColor,
      };
    },
    ANSWER_AREA: ({ settings }) =>
      computeEffectiveSettings(
        settings,
        pinThemePinComponentSettings?.ANSWER_AREA,
        defaultPinThemePinComponentSettings?.ANSWER_AREA,
        pinThemePinComponentSchemaReturnTypes.ANSWER_AREA
      ),
    SPINNER: ({ settings }) => {
      const spinnerSettings = computeEffectiveSettings(
        settings,
        pinThemePinComponentSettings?.SPINNER,
        defaultPinThemePinComponentSettings?.SPINNER,
        pinThemePinComponentSchemaReturnTypes.SPINNER
      );

      return {
        ...spinnerSettings,
        sliceColors: merge(
          {},
          {
            ...(pinThemePinComponentSettings?.SPINNER?.sliceColors ??
              defaultPinThemePinComponentSettings?.SPINNER.sliceColors ??
              {}),
          },
          { ...settings.sliceColors }
        ),
      };
    },
    TIMER: ({ settings }) =>
      computeEffectiveSettings(
        settings,
        pinThemePinComponentSettings?.TIMER,
        defaultPinThemePinComponentSettings?.TIMER,
        pinThemePinComponentSchemaReturnTypes.TIMER
      ),
    UNEDITABLE_SHAPE: ({ settings }) =>
      computeEffectiveSettings(
        settings,
        pinThemePinComponentSettings?.UNEDITABLE_SHAPE,
        defaultPinThemePinComponentSettings?.UNEDITABLE_SHAPE,
        pinThemePinComponentSchemaReturnTypes.UNEDITABLE_SHAPE
      ),
    DROP_ZONE: ({ settings, textEditorHandler, isTextEditMode }) => {
      const dropzoneSettings = computeEffectiveSettings(
        settings,
        pinThemePinComponentSettings?.DROP_ZONE,
        defaultPinThemePinComponentSettings?.DROP_ZONE,
        pinThemePinComponentSchemaReturnTypes.DROP_ZONE
      );
      const activeTypography = getTypography(settings, typographyRecord);

      const fontFamilyCursorValue = textEditorHandler?.fontFamily.get('');
      const innerFontFamilyValue = dropzoneSettings.fontFamily ?? activeTypography?.fontFamily;
      const fontFamilyValue = isTextEditMode && fontFamilyCursorValue ? fontFamilyCursorValue : innerFontFamilyValue;

      const innerFontSizeValue = dropzoneSettings.fontSize ?? activeTypography?.fontSize;
      const fontSizeCursorValue = textEditorHandler?.fontSize.get();
      const fontSize = isTextEditMode && fontSizeCursorValue ? fontSizeCursorValue : innerFontSizeValue;

      const fontColorCursorValue = textEditorHandler?.color.get('');
      const fontColor = isTextEditMode && fontColorCursorValue ? fontColorCursorValue : dropzoneSettings.fontColor;

      return {
        ...dropzoneSettings,
        typographyKey: dropzoneSettings.typographyKey ?? 'normalText',
        fontFamily: fontFamilyValue,
        fontSize,
        fontColor,
      };
    },
  };

  return handlers;
};

function computeEffectiveGenericObject<O extends object, CommonKey extends keyof O>(
  settingKeys: CommonKey[],
  settingsObject?: O,
  pinThemeSettingsObject?: O,
  pinThemeDefaultSettingsObject?: O
): O {
  return settingKeys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: settingsObject?.[key] ?? pinThemeSettingsObject?.[key] ?? pinThemeDefaultSettingsObject?.[key],
    }),
    {} as O
  );
}

function computeEffectiveSettings<
  P extends PinComponent,
  S extends P['settings'],
  ThemeSettings extends PinThemePinComponentSettings[P['type']],
  SettingKeys extends keyof S & keyof ThemeSettings,
>(
  pinComponentSettings: S,
  pinComponentThemePinSettings?: ThemeSettings,
  pinComponentDefaultThemeSettings?: ThemeSettings,
  settingKeys: SettingKeys[] = []
): S {
  const effectiveThemeSettings = settingKeys.reduce(
    (acc, key) => ({
      ...acc,
      [key]:
        pinComponentSettings[key] ?? pinComponentThemePinSettings?.[key] ?? pinComponentDefaultThemeSettings?.[key],
    }),
    {} as ThemeSettings
  );

  return {
    ...pinComponentSettings,
    ...effectiveThemeSettings,
  };
}
